/**
 * Leaderboard API - Type-safe leaderboard endpoints
 * Matches backend: app/api/v1/routes/leaderboard.py
 */

import { apiClient } from './apiClient';
import type { LeaderboardEntry, LeaderboardResponse, LeaderboardRequest } from '../../types/rest';

/**
 * Get global leaderboard
 * GET /api/v1/leaderboard
 * Query: limit (default 100, max 200), offset
 * 200 -> LeaderboardResponse
 */
export async function getLeaderboard(params?: LeaderboardRequest): Promise<LeaderboardResponse> {
  const response = await apiClient.get<LeaderboardResponse>('/leaderboard', { params });
  return response.data;
}

/**
 * Get user's rank in leaderboard
 * GET /api/v1/leaderboard/me
 * 200 -> { rank, score, stage }
 * 401 -> unauthorized
 * 404 -> user not found
 */
export async function getMyRank(): Promise<{ rank: number; score: number; stage: number }> {
  const response = await apiClient.get<{ rank: number; score: number; stage: number }>('/leaderboard/me');
  return response.data;
}

/**
 * Get leaderboard entries around a specific rank
 * GET /api/v1/leaderboard/around/:rank
 * Query: range (default 10)
 * 200 -> LeaderboardEntry[]
 * 404 -> not found
 */
export async function getLeaderboardAround(
  rank: number,
  range: number = 10
): Promise<LeaderboardEntry[]> {
  const response = await apiClient.get<LeaderboardEntry[]>(`/leaderboard/around/${rank}`, {
    params: { range },
  });
  return response.data;
}

/**
 * Get leaderboard by stage (FTPES tier)
 * GET /api/v1/leaderboard/stage/:stage
 * Query: limit, offset
 * 200 -> LeaderboardResponse
 */
export async function getLeaderboardByStage(
  stage: number,
  params?: { limit?: number; offset?: number }
): Promise<LeaderboardResponse> {
  const response = await apiClient.get<LeaderboardResponse>(`/leaderboard/stage/${stage}`, { params });
  return response.data;
}

/**
 * Search leaderboard by username
 * GET /api/v1/leaderboard/search
 * Query: q (username), limit (default 10)
 * 200 -> LeaderboardEntry[]
 */
export async function searchLeaderboard(
  query: string,
  limit: number = 10
): Promise<LeaderboardEntry[]> {
  const response = await apiClient.get<LeaderboardEntry[]>('/leaderboard/search', {
    params: { q: query, limit },
  });
  return response.data;
}

/**
 * Leaderboard error codes
 */
export const LEADERBOARD_ERROR_CODES = {
  NOT_FOUND: 'not_found',
  INVALID_STAGE: 'invalid_stage',
  INVALID_PARAMS: 'invalid_params',
} as const;

export type LeaderboardErrorCode = typeof LEADERBOARD_ERROR_CODES[keyof typeof LEADERBOARD_ERROR_CODES];